import { Navigate, Route, Routes, useLocation, useParams } from 'react-router'

import { getGame } from '@hazel/shared'

import { useSession } from './auth/session-context.js'
import { AppLayout } from './layouts/AppLayout.js'
import { ScaleProvider } from './layouts/ScaleProvider.js'
import { TvLayout } from './layouts/TvLayout.js'
import { Hangman } from './games/Hangman.js'
import { Maths } from './games/Maths.js'
import { Memory } from './games/Memory.js'
import { Scramble } from './games/Scramble.js'
import { Riddles } from './games/Riddles.js'
import { Mastermind } from './games/Mastermind.js'
import { Mosaic } from './games/Mosaic.js'
import { Sudoku } from './games/Sudoku.js'
import { Trolley } from './games/Trolley.js'
import { Accounts } from './pages/Accounts.js'
import { Host } from './pages/Host.js'
import { Join } from './pages/Join.js'
import { GamePicker } from './pages/GamePicker.js'
import { Login } from './pages/Login.js'
import { ParentView } from './pages/ParentView.js'
import { Profile } from './pages/Profile.js'
import { NotFound } from './pages/NotFound.js'
import { StyleGuide } from './pages/StyleGuide.js'

function Loading() {
  return (
    <p role="status" className="text-ink-muted py-16 text-center">
      Just a moment…
    </p>
  )
}

/**
 * Gate for routes that need an account.
 *
 * Renders a loading state rather than redirecting while the session is still
 * being fetched — otherwise every refresh bounces a logged-in player to the
 * login screen for a moment before bouncing them back.
 */
function RequireAccount({ children }: { children: React.ReactNode }) {
  const { status } = useSession()
  const location = useLocation()

  if (status === 'loading') return <Loading />

  // Where she was going, so logging in finishes the journey rather than
  // starting a new one. Being dropped back at the beginning is a small insult
  // when the app knows perfectly well where she was headed.
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  return <>{children}</>
}

/**
 * Gate for the account screens.
 *
 * A child reaching /accounts gets the 404 rather than a "forbidden" page — no
 * reason to advertise that a parents-only screen exists. The server enforces
 * this too; this only keeps the UI honest.
 */
function RequireParent({ children }: { children: React.ReactNode }) {
  const { status, user } = useSession()

  if (status === 'loading') return <Loading />
  if (status === 'anonymous') return <Navigate to="/login" replace />
  if (user?.role !== 'parent') return <NotFound />
  return <>{children}</>
}

/** Validates the :gameKey segment before anything tries to load a game. */
function PlayRoute() {
  const { gameKey } = useParams()
  const game = gameKey ? getGame(gameKey) : undefined

  if (!game) return <NotFound />

  /*
   * A switch with a `never` at the end, not an if-chain with a placeholder.
   *
   * Every game in the roster is built now, so a key with no screen is a game
   * somebody added and forgot to wire up — and the old fallback answered that
   * with a tidy "coming soon" page which looks entirely deliberate. The
   * compiler refusing to build is the louder and more useful answer.
   */
  switch (game.key) {
    case 'hangman':
      return <Hangman />
    case 'maths':
      return <Maths />
    case 'memory':
      return <Memory />
    case 'scramble':
      return <Scramble />
    case 'trolley':
      return <Trolley />
    case 'riddles':
      return <Riddles />
    case 'sudoku':
      return <Sudoku />
    case 'mosaic':
      return <Mosaic />
    case 'mastermind':
      return <Mastermind />
  }

  /*
   * The `never` is the whole point, and it was missing from the first version
   * of this switch while the comment above claimed the compiler was checking
   * it. It is not: `PlayRoute` has no declared return type, JSX tolerates a
   * component that implicitly returns undefined, and `noImplicitReturns` is
   * off — so a seventh game left out of the cases compiled cleanly and
   * rendered a blank page. Assigning to `never` is what actually refuses it.
   */
  const unreachable: never = game.key
  throw new Error(`No screen for ${String(unreachable)}`)
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Host display: no chrome, TV scale, and deliberately reachable without
          an account — the TV is a screen, not a player. */}
      <Route element={<TvLayout />}>
        <Route path="/host" element={<Host />} />
      </Route>

      {/*
        The chosen size belongs to the ordinary app, and the provider is
        wrapped around it rather than around the whole router.

        Above the router it also wrapped the host display — which is a
        television by definition rather than by choice — so merely opening
        /host wrote a size into that browser's memory, and every page it
        afterwards showed inherited it. Nothing broke, because the host
        hardcodes its own scale, but the isolation three comments claimed was
        not there.
      */}
      <Route
        element={
          <ScaleProvider>
            <AppLayout />
          </ScaleProvider>
        }
      >
        {/* The games need an account. Not to be strict — because a round that
            is not recorded is a round that taught the app nothing about her,
            and because the front door is not the place for the generosity that
            makes sense in a room. */}
        <Route
          index
          element={
            <RequireAccount>
              <GamePicker />
            </RequireAccount>
          }
        />
        <Route
          path="/play/:gameKey"
          element={
            <RequireAccount>
              <PlayRoute />
            </RequireAccount>
          }
        />

        {/* Guests can join a room from a phone; results simply are not recorded. */}
        <Route path="/join" element={<Join />} />
        <Route path="/join/:code" element={<Join />} />

        <Route path="/login" element={<Login />} />
        <Route
          path="/profile"
          element={
            <RequireAccount>
              <Profile />
            </RequireAccount>
          }
        />

        <Route
          path="/accounts"
          element={
            <RequireParent>
              <Accounts />
            </RequireParent>
          }
        />

        <Route
          path="/progress"
          element={
            <RequireParent>
              <ParentView />
            </RequireParent>
          }
        />

        <Route path="/styleguide" element={<StyleGuide />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}
